import { Box, Link, Typography, useTheme } from '@mui/material';
import type { MouseEvent } from 'react';
import type { SectionAction } from '../types';

type Props = {
  overline?: string;
  title?: string;
  subtitle?: string;
  aside?: string;
  actions?: SectionAction[];
  onActionClick?: (action: SectionAction) => void;
};

const formatActionLabel = (label: string) =>
  label.includes('↓') || label.includes('→') ? label : `${label} →`;

export default function SectionHeader({
  overline,
  title,
  subtitle,
  aside,
  actions = [],
  onActionClick,
}: Props) {
  const theme = useTheme();
  if (!overline && !title) return null;

  const handleActionClick = (event: MouseEvent<HTMLAnchorElement>, action: SectionAction) => {
    if (!onActionClick || !action.navigation) return;
    event.preventDefault();
    onActionClick(action);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        mb: title ? 2 : 0.5,
      }}
    >
      <Box>
        {overline ? (
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'text.secondary',
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {overline}
          </Typography>
        ) : null}
        {title ? (
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
        ) : null}
        {subtitle ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {aside || actions.length ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {aside ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                fontSize: 13,
                fontStyle: 'italic',
              }}
            >
              {aside}
            </Typography>
          ) : null}
          {actions.length ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {actions.map(action => (
                <Link
                  key={action.label}
                  href={action.href || '#'}
                  underline="hover"
                  onClick={event => handleActionClick(event, action)}
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                    cursor: action.navigation && onActionClick ? 'pointer' : undefined,
                  }}
                >
                  {formatActionLabel(action.label)}
                </Link>
              ))}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
